import React, { useState, useEffect } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Box,
    Typography,
    Alert
} from '@mui/material'
import { AxiosWithAuth } from '../../Utilities/authenticationService'

const NewStoreModal = ({ open, onClose, onSuccess }) => {
    const [storeTypes, setStoreTypes] = useState([])
    const [companies, setCompanies] = useState([])
    const [loading, setLoading] = useState(true)
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState('')
    
    const [formData, setFormData] = useState({
        name: '',
        seg_code: '',
        store_type_id: '',
        company_id: ''
    })

    useEffect(() => {
        if (open) {
            setLoading(true)
            setError('')
            Promise.all([
                AxiosWithAuth.get('/store_types'),
                AxiosWithAuth.get('/companies')
            ])
                .then(([storeTypesRes, companiesRes]) => {
                    setStoreTypes(storeTypesRes.data)
                    setCompanies(companiesRes.data)
                    setLoading(false)
                })
                .catch(err => {
                    console.error(err)
                    setError('データの読み込みに失敗しました')
                    setLoading(false)
                })
        }
    }, [open])

    const handleClose = () => {
        setFormData({
            name: '',
            seg_code: '',
            store_type_id: '',
            company_id: ''
        })
        setError('')
        onClose()
    }

    const handleChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }))
    }

    const handleSubmit = async () => {
        if (!formData.name || !formData.seg_code || !formData.store_type_id) {
            setError('店舗名、店舗コード、店舗タイプは必須です')
            return
        }

        setSubmitting(true)
        setError('')

        try {
            const response = await AxiosWithAuth.post('/stores', {
                store: {
                    name: formData.name,
                    seg_code: formData.seg_code,
                    store_type_id: formData.store_type_id,
                    company_id: formData.company_id || null
                }
            })
            onSuccess(response.data)
            handleClose()
        } catch (err) {
            console.error('Failed to create store:', err)
            if (err.response?.data?.errors) {
                setError(err.response.data.errors.join(', '))
            } else if (err.response?.data?.error) {
                setError(err.response.data.error)
            } else {
                setError('店舗の作成に失敗しました')
            }
        } finally {
            setSubmitting(false)
        }
    }

    const isValid = formData.name && formData.seg_code && formData.store_type_id

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', pb: 2 }}>
                <Typography variant="h6" component="span">
                    新規店舗登録
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 3, pb: 2 }}>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1 }}>
                    <TextField
                        label="店舗名"
                        value={formData.name}
                        onChange={handleChange('name')}
                        required
                        fullWidth
                        placeholder="例: 横浜店"
                    />

                    <TextField
                        label="店舗コード"
                        value={formData.seg_code}
                        onChange={handleChange('seg_code')}
                        required
                        fullWidth
                        placeholder="例: 12345"
                        helperText="ユニークな店舗識別コード"
                    />

                    <FormControl fullWidth required>
                        <InputLabel>店舗タイプ</InputLabel>
                        <Select
                            value={formData.store_type_id}
                            onChange={handleChange('store_type_id')}
                            label="店舗タイプ"
                            disabled={loading}
                        >
                            {storeTypes.map(type => (
                                <MenuItem key={type.id} value={type.id}>
                                    {type.type_name} - {type.brand_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <FormControl fullWidth>
                        <InputLabel>会社（任意）</InputLabel>
                        <Select
                            value={formData.company_id}
                            onChange={handleChange('company_id')}
                            label="会社（任意）"
                            disabled={loading}
                        >
                            <MenuItem value="">
                                <em>未選択</em>
                            </MenuItem>
                            {companies.map(company => (
                                <MenuItem key={company.id} value={company.id}>
                                    {company.company_name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
                <Button onClick={handleClose} color="inherit" disabled={submitting}>
                    キャンセル
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={!isValid || submitting || loading}
                    sx={{ minWidth: 80 }}
                >
                    {submitting ? '作成中...' : '作成'}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default NewStoreModal

